const SECRET_PARAMS = new Set([
  "token",
  "code",
  "state",
  "ticket",
  "pending",
  "nonce",
  "access_token",
  "refresh_token",
  "reset_password_token",
  "change_password_token",
  "magic_link_token",
]);

const REDACTED = "[REDACTED]";

const redactParams = (params: URLSearchParams) => {
  let changed = false;
  for (const key of Array.from(params.keys())) {
    if (SECRET_PARAMS.has(key.toLowerCase())) {
      params.set(key, REDACTED);
      changed = true;
    }
  }
  return changed;
};

// Works for absolute URLs, paths with a query string and bare fragments.
export function scrubUrl(value: string): string {
  if (!value || (!value.includes("?") && !value.includes("#"))) {
    return value;
  }

  try {
    const absolute = /^[a-z][a-z0-9+.-]*:/i.test(value);
    const url = new URL(value, "https://placeholder.invalid");
    let changed = redactParams(url.searchParams);

    if (url.hash) {
      const fragment = new URLSearchParams(url.hash.slice(1));
      if (redactParams(fragment)) {
        url.hash = fragment.toString();
        changed = true;
      }
    }

    if (!changed) {
      return value;
    }
    return absolute
      ? url.toString()
      : `${url.pathname}${url.search}${url.hash}`;
  } catch {
    return value;
  }
}

export function isSecretParam(key: string) {
  return SECRET_PARAMS.has(key.toLowerCase());
}

export const secretParams = Array.from(SECRET_PARAMS);

type SentryLikeEvent = {
  request?: {
    url?: string;
    query_string?: unknown;
    headers?: Record<string, string>;
  };
  transaction?: string;
  breadcrumbs?: { data?: Record<string, unknown> }[];
};

export function scrubSentryEvent<T extends SentryLikeEvent>(event: T): T {
  if (event.request?.url) {
    event.request.url = scrubUrl(event.request.url);
  }
  if (typeof event.request?.query_string === "string") {
    event.request.query_string = scrubUrl(
      `?${event.request.query_string}`,
    ).slice(1);
  }
  if (event.request?.headers) {
    delete event.request.headers.Authorization;
    delete event.request.headers.authorization;
  }
  if (event.transaction) {
    event.transaction = scrubUrl(event.transaction);
  }
  for (const breadcrumb of event.breadcrumbs ?? []) {
    for (const field of ["url", "from", "to"]) {
      const value = breadcrumb.data?.[field];
      if (typeof value === "string") {
        breadcrumb.data![field] = scrubUrl(value);
      }
    }
  }
  return event;
}

const scrubParams = (value: string) => {
  const params = new URLSearchParams(value);
  return redactParams(params) ? params.toString() : value;
};

const scrubAttribute = (key: string, value: unknown): unknown => {
  if (typeof value === "string") {
    // These two arrive without their leading "?" or "#".
    return key === "url.query" || key === "url.fragment"
      ? scrubParams(value)
      : scrubUrl(value);
  }
  if (Array.isArray(value)) {
    return value.map(item =>
      typeof item === "string" ? scrubUrl(item) : item,
    );
  }
  if (value && typeof value === "object" && "value" in value) {
    return { ...value, value: scrubAttribute(key, value.value) };
  }
  return value;
};

type SentryLikeSpan = {
  name: string;
  attributes: Record<string, unknown>;
};

export function scrubSentrySpan<T extends SentryLikeSpan>(span: T): T {
  span.name = scrubUrl(span.name);
  for (const [key, value] of Object.entries(span.attributes)) {
    span.attributes[key] = scrubAttribute(key, value);
  }
  return span;
}
